import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { PageHeader, Card, Loader } from "@/components/ui/Primitives";
import { Send, Sparkles, ArrowUpRight, Loader2, Building2, Wallet, AlertCircle } from "lucide-react";
import { api, formatCents } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MessageBubble } from "@/components/SmartOperatorBubble";

const SUGGESTED = [
  "How is my portfolio doing this month?",
  "What's my STP rate?",
  "Which units are vacant?",
  "Any unpaid rent right now?",
  "Open maintenance tickets?",
  "Draft a rent-increase notice for Unit 3B",
];

export default function SmartOperator() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem("crc_smart_operator_sid") || ""; } catch (_) { return ""; }
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Hydrate history on mount.
  useEffect(() => {
    if (!sessionId) return;
    let mounted = true;
    api.get(`/smart-operator/sessions/${sessionId}`).then((r) => {
      if (!mounted) return;
      const rows = (r.data?.messages || []).map((m) => ({
        role: m.role, content: m.content, actions: m.actions || [],
      }));
      setMessages(rows);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [sessionId]);

  if (user && user.role !== "landlord") return <Navigate to="/dashboard" replace />;

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/smart-operator/ask", {
        message: msg,
        session_id: sessionId || undefined,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem("crc_smart_operator_sid", data.session_id); } catch (_) { /* noop */ }
      }
      setSnapshot(data.snapshot || null);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, actions: data.actions || [] }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't reach Smart Operator";
      setMessages((m) => [...m, { role: "assistant", content: `Sorry — ${detail}.`, actions: [] }]);
    } finally {
      setSending(false);
    }
  };

  if (!user) return <Loader />;

  return (
    <div data-testid="smart-operator-page">
      <PageHeader
        title="Smart Operator"
        subtitle="Ask anything about your portfolio. Read-only — answers and shortcuts, no actions taken without you."
      />

      {snapshot && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="smart-operator-snapshot">
          <SnapshotChip label="Properties" value={snapshot.properties} icon={Building2} />
          <SnapshotChip label="Active leases" value={snapshot.active_leases} icon={Building2} />
          <SnapshotChip label="Rent this month" value={formatCents((snapshot.rent_collected_month_cad || 0) * 100)} icon={Wallet} />
          <SnapshotChip label="STP rate" value={snapshot.stp_rate_pct != null ? `${snapshot.stp_rate_pct}%` : "—"} icon={Sparkles} />
        </div>
      )}

      <Card className="overflow-hidden flex flex-col h-[640px]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-3 bg-slate-50">
          {messages.length === 0 && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600 leading-relaxed max-w-2xl">
                Welcome back. Pick a question to get started, or ask anything — I have your live portfolio numbers in context.
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {SUGGESTED.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`smart-operator-page-suggest-${i}`}
                    onClick={() => send(s)}
                    className="text-left px-4 py-3 rounded-xl bg-white border border-slate-200 text-sm text-slate-700 hover:border-emerald-300 hover:bg-emerald-50/40"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageBubble key={i} m={m} />
          ))}

          {sending && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3 h-3 animate-spin" /> Smart Operator is thinking…
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-slate-200 p-4 bg-white flex items-end gap-2"
        >
          <textarea
            data-testid="smart-operator-page-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="Ask anything — vacancies, rent due, STP rate, how to draft a notice…"
            className="flex-1 resize-none px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 min-h-[44px] max-h-[160px]"
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            data-testid="smart-operator-page-send"
            className="p-3 rounded-lg bg-emerald-600 text-white disabled:opacity-50 hover:bg-emerald-700 flex-none"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </Card>

      <div className="mt-4 text-xs text-slate-500 flex items-center gap-1.5">
        <AlertCircle className="w-3 h-3" />
        Smart Operator is read-only today. It can answer and point you to the right page, but it never sends notices, drafts leases, or moves money without your confirmation.
      </div>
    </div>
  );
}

function SnapshotChip({ label, value, icon: Icon }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500">{label}</div>
          <div className="text-sm font-bold text-slate-900 truncate">{value ?? "—"}</div>
        </div>
      </div>
    </Card>
  );
}
